import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import { settingsBase } from './settingsPaths';
import '../tender/CustomerMaster.scss';
import '../tender/PersonnelPage.scss';
import './EmailSettingsPage.scss';

const EMPTY = {
  enabled: false,
  fromName: '',
  fromAddress: '',
  replyTo: '',
  host: '',
  port: '587',
  security: 'starttls',
  username: '',
  password: ''
};

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const EmailSettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const base = settingsBase(location.pathname);
  const [form, setForm] = useState(EMPTY);
  const [passwordConfigured, setPasswordConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMode, setTestMode] = useState('');
  const [testTo, setTestTo] = useState(user?.email || '');
  const [message, setMessage] = useState(null);

  useDocumentTitle('Email');

  useEffect(() => {
    let cancelled = false;
    platformAPI.getMailSettings()
      .then((response) => {
        if (cancelled) return;
        const mail = response.data?.data?.mail || {};
        setPasswordConfigured(Boolean(mail.passwordConfigured));
        setForm({
          enabled: Boolean(mail.enabled),
          fromName: mail.fromName || '',
          fromAddress: mail.fromAddress || '',
          replyTo: mail.replyTo || '',
          host: mail.host || '',
          port: String(mail.port || 587),
          security: mail.security || 'starttls',
          username: mail.username || '',
          password: ''
        });
      })
      .catch((error) => {
        if (!cancelled) setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to load email settings.' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const setField = (name) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const response = await platformAPI.updateMailSettings({
        ...form,
        port: Number(form.port) || 587,
        password: form.password
      });
      const mail = response.data?.data?.mail || {};
      setPasswordConfigured(Boolean(mail.passwordConfigured));
      setForm((current) => ({ ...current, password: '' }));
      setMessage({ variant: 'success', text: 'Email settings saved.' });
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to save email settings.' });
    } finally {
      setSaving(false);
    }
  };

  const test = async (to) => {
    const recipient = String(to || '').trim();
    setTesting(true);
    setTestMode(recipient ? 'send' : 'check');
    setMessage(null);
    try {
      const response = await platformAPI.testMailSettings(recipient ? { to: recipient } : {});
      setMessage({
        variant: 'success',
        text: response.data?.message || (recipient ? `A test message was sent to ${recipient}.` : 'The mail server accepted the connection.')
      });
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'The mail server rejected the request.' });
    } finally {
      setTesting(false);
      setTestMode('');
    }
  };

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Alert variant="warning">A tender administrator maintains email settings.</Alert>;
  }

  if (loading) {
    return (
      <div className="py-5 text-center text-muted">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading email settings…
      </div>
    );
  }

  return (
    <div className="cust-page">
      <form className="cust-record pers-record" onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate(base)}>
              <BiArrowBack />
              Settings
            </button>
            <div className="cust-mast__row">
              <span className="cust-mark" aria-hidden>@</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">Settings</p>
                <h1>Email</h1>
                <div className="cust-mast__meta">
                  <span className="cust-chip">{form.enabled ? 'Enabled' : 'Not enabled'}</span>
                  <span className="cust-chip">{passwordConfigured ? 'Password stored' : 'No password yet'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            <Button size="sm" variant="primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </header>

        {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}

        <section className="cust-section">
          <div className="cust-section__head">
            <span>01</span>
            <div>
              <h2>Sender</h2>
              <p>The name and address people see when the workspace writes to them.</p>
            </div>
          </div>
          <div className="cust-fields">
            <Field label="Send mail" wide={12}>
              <Form.Check
                type="switch"
                id="mail-enabled"
                label={form.enabled ? 'The workspace may send mail' : 'Sending is switched off'}
                checked={form.enabled}
                onChange={setField('enabled')}
              />
            </Field>
            <Field label="From name" wide={6}>
              <Form.Control value={form.fromName} onChange={setField('fromName')} placeholder="Dhruv Consultancy" />
            </Field>
            <Field label="From address" wide={6}>
              <Form.Control type="email" value={form.fromAddress} onChange={setField('fromAddress')} placeholder="notices@dhruvconsultancy.in" />
            </Field>
            <Field label="Reply-to" wide={6}>
              <Form.Control type="email" value={form.replyTo} onChange={setField('replyTo')} placeholder="Optional" />
            </Field>
          </div>
        </section>

        <section className="cust-section">
          <div className="cust-section__head">
            <span>02</span>
            <div>
              <h2>Mail server</h2>
              <p>The mailbox that carries those messages. Leave the password blank to keep the one already stored.</p>
            </div>
          </div>
          <div className="cust-fields">
            <Field label="Host" wide={6}>
              <Form.Control value={form.host} onChange={setField('host')} placeholder="smtp.office365.com" />
            </Field>
            <Field label="Port" wide={3}>
              <Form.Control value={form.port} onChange={setField('port')} inputMode="numeric" />
            </Field>
            <Field label="Security" wide={3}>
              <Form.Select value={form.security} onChange={setField('security')}>
                <option value="starttls">STARTTLS</option>
                <option value="ssl">SSL</option>
                <option value="none">None</option>
              </Form.Select>
            </Field>
            <Field label="Username" wide={6}>
              <Form.Control value={form.username} onChange={setField('username')} autoComplete="off" />
            </Field>
            <Field label="Password" wide={6}>
              <Form.Control
                type="password"
                value={form.password}
                onChange={setField('password')}
                autoComplete="new-password"
                placeholder={passwordConfigured ? 'Leave blank to keep the stored password' : 'Mailbox password'}
              />
            </Field>
          </div>
        </section>

        <section className="cust-section">
          <div className="cust-section__head">
            <span>03</span>
            <div>
              <h2>Try it</h2>
              <p>Check that the server answers, or send a short message to a real inbox. Save first so the test uses the stored settings.</p>
            </div>
          </div>
          <div className="cust-fields">
            <Field label="Send a test to" wide={8}>
              <Form.Control
                type="email"
                value={testTo}
                onChange={(event) => setTestTo(event.target.value)}
                placeholder={user?.email || 'name@company.com'}
              />
            </Field>
            <div className="cust-field cust-field--4 mail-try__actions">
              <Button size="sm" variant="outline-primary" type="button" onClick={() => test()} disabled={testing || saving}>
                {testing && testMode === 'check' ? 'Checking…' : 'Test connection'}
              </Button>
              <Button size="sm" variant="primary" type="button" onClick={() => test(testTo || user?.email || '')} disabled={testing || saving}>
                {testing && testMode === 'send' ? 'Sending…' : 'Send test mail'}
              </Button>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
};

export default EmailSettingsPage;
